import { useMemo, useState } from "react";
import { Stack, IconButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import styles from "./Answer.module.css";
import { ChatAppResponse } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";

interface Props {
    answer: ChatAppResponse;
    index: number;
    isSelected?: boolean;
    isStreaming: boolean;
    onThoughtProcessClicked: () => void;
}

export const Answer = ({ answer, index, isSelected, isStreaming, onThoughtProcessClicked }: Props) => {
    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer, isStreaming), [answer]);
    const { t } = useTranslation();
    const suggestedVersion = answer.context?.suggested_version;
    const classification = answer.context?.classification;
    // Strip the suggested version section using language-independent markers BEFORE sanitization
    let htmlBeforeSanitize = parsedAnswer.answerHtml;
    if (suggestedVersion && (suggestedVersion.subject || suggestedVersion.body)) {
        htmlBeforeSanitize = htmlBeforeSanitize.replace(/<!-- SV_START -->[\s\S]*?<!-- SV_END -->/i, "");
    }
    const sanitizedAnswerHtml = DOMPurify.sanitize(htmlBeforeSanitize);
    const [copied, setCopied] = useState(false);

    // Use the structured compliance_status from the backend (language-independent)
    const complianceStatus = useMemo(() => {
        const status = answer.context?.compliance_status;
        if (status === "requires_review" || status === "non_compliant") {
            return "requires_review";
        }
        return "no_review";
    }, [answer]);

    const currentTime = new Date().toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    });

    const handleCopy = () => {
        // Build the clipboard text: commentary (if non-empty) + suggested email (if available).
        // sanitizedAnswerHtml has the SV_START/SV_END block stripped, so it contains only AI commentary.
        const tmp = document.createElement("div");
        tmp.innerHTML = sanitizedAnswerHtml;
        const commentaryText = tmp.textContent ?? tmp.innerText ?? "";

        const parts: string[] = [];
        if (commentaryText.trim()) parts.push(commentaryText);
        if (suggestedVersion && (suggestedVersion.subject || suggestedVersion.body)) {
            if (suggestedVersion.subject) parts.push(`Subject: ${suggestedVersion.subject}`);
            if (suggestedVersion.body) parts.push(suggestedVersion.body);
        }
        const textToCopy = parts.join("\n\n");

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(err => console.error("Failed to copy text: ", err));
    }; // Use structured suggested_version from backend if present
    // suggestedVersion already defined above
    const [copiedSuggested, setCopiedSuggested] = useState(false);
    const [copiedSubject, setCopiedSubject] = useState(false);
    const [copiedBody, setCopiedBody] = useState(false);

    const handleCopySuggested = () => {
        if (!suggestedVersion) return;
        const parts: string[] = [];
        if (suggestedVersion.subject) parts.push(`Subject: ${suggestedVersion.subject}`);
        if (suggestedVersion.body) parts.push(suggestedVersion.body);
        navigator.clipboard
            .writeText(parts.join("\n\n"))
            .then(() => {
                setCopiedSuggested(true);
                setTimeout(() => setCopiedSuggested(false), 2000);
            })
            .catch(err => console.error("Failed to copy suggested version: ", err));
    };

    const handleCopySubject = () => {
        if (!suggestedVersion?.subject) return;
        navigator.clipboard
            .writeText(suggestedVersion.subject)
            .then(() => {
                setCopiedSubject(true);
                setTimeout(() => setCopiedSubject(false), 2000);
            })
            .catch(err => console.error("Failed to copy subject: ", err));
    };

    const handleCopyBody = () => {
        if (!suggestedVersion?.body) return;
        navigator.clipboard
            .writeText(suggestedVersion.body)
            .then(() => {
                setCopiedBody(true);
                setTimeout(() => setCopiedBody(false), 2000);
            })
            .catch(err => console.error("Failed to copy body: ", err));
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <div className={`${styles.emailHeader} ${complianceStatus === "requires_review" ? styles.headerYellow : styles.headerGreen}`}>
                <div className={styles.headerLeft}>
                    <span className={styles.emailIcon}>🤖</span>
                    <div className={styles.emailMeta}>
                        <div className={styles.emailTitle}>{t("email.responseBanner")}</div>
                        <div className={styles.emailTimestamp}>{currentTime}</div>
                    </div>
                </div>
                <div className={styles.emailActions}>
                    <IconButton
                        className={styles.actionButton}
                        iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                        title={copied ? t("tooltips.copied") : t("tooltips.copy")}
                        ariaLabel={copied ? t("tooltips.copied") : t("tooltips.copy")}
                        onClick={handleCopy}
                    />
                    <IconButton
                        className={styles.actionButton}
                        iconProps={{ iconName: "Lightbulb" }}
                        title={t("tooltips.showThoughtProcess")}
                        ariaLabel={t("tooltips.showThoughtProcess")}
                        onClick={() => onThoughtProcessClicked()}
                        disabled={!answer.context.thoughts?.length || isStreaming}
                    />
                </div>
            </div>
            <Stack.Item grow>
                <div className={styles.emailContent}>
                    <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
                        {sanitizedAnswerHtml}
                    </ReactMarkdown>{" "}
                    {suggestedVersion && (suggestedVersion.subject || suggestedVersion.body) && (
                        <div className={styles.suggestedEmail}>
                            <div className={styles.suggestedEmailHeader}>
                                <div className={styles.suggestedEmailIcon}>✉️</div>
                                <div style={{ flex: 1 }}>
                                    <div className={styles.suggestedEmailTitle}>
                                        {classification === "2" ? t("email.originalBanner") : t("email.suggestedBanner")}
                                    </div>
                                    {suggestedVersion.subject && (
                                        <div className={styles.suggestedEmailSubject}>
                                            <strong>{t("email.subject")}:</strong> {suggestedVersion.subject}
                                        </div>
                                    )}
                                </div>
                                {suggestedVersion.subject && (
                                    <IconButton
                                        className={styles.suggestedCopyButton}
                                        iconProps={{ iconName: copiedSubject ? "CheckMark" : "Copy" }}
                                        title={copiedSubject ? t("tooltips.copied") : t("tooltips.copySubject")}
                                        ariaLabel={copiedSubject ? t("tooltips.copied") : t("tooltips.copySubject")}
                                        onClick={handleCopySubject}
                                    />
                                )}
                            </div>
                            {suggestedVersion.body && (
                                <div className={styles.suggestedEmailBody}>
                                    <div style={{ marginBottom: "0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <strong>{t("email.body")}</strong>
                                        <IconButton
                                            className={styles.suggestedCopyButton}
                                            iconProps={{ iconName: copiedBody ? "CheckMark" : "Copy" }}
                                            title={copiedBody ? t("tooltips.copied") : t("tooltips.copyBody")}
                                            ariaLabel={copiedBody ? t("tooltips.copied") : t("tooltips.copyBody")}
                                            onClick={handleCopyBody}
                                        />
                                    </div>
                                    <div>{suggestedVersion.body}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Stack.Item>
        </Stack>
    );
};
